import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { UserRole } from "../enums/UserRole";
import { useBrowserContext } from "../hooks/useBrowserContext";
import { useUserContext } from "../hooks/useUserContext";
import { Auth } from "../utils/Auth";
import { getLocalStorage } from "../utils/LocalStorage.utils";
import { Upper } from "../utils/String.utils";
import { PermissionProvider } from "./PermissionProvider";
export const AppContext = createContext(undefined);
export const PrivateRoutes = () => {
    const meStorageKey = `${import.meta.env.VITE_SESSION_PREFIX}-me-data`;
    const auth = useMemo(() => new Auth(), []);
    const location = useLocation();
    // User Context
    const { userId, userRole, userName, userGroups, organizationId, originalOrganizationId, isTherapproveAdmin, isCaseManager, isMasquerading, isTokenExpired } = useUserContext();
    const storedMe = getLocalStorage(meStorageKey);
    // Browser Context
    const { isLocalhost, isAdminApp, isOrganizationApp } = useBrowserContext();
    const resolvedMe = storedMe ?? null;
    const resolvedUserId = resolvedMe?.id ?? userId;
    const resolvedUserName = useMemo(() => {
        if (resolvedMe?.name) {
            const [firstName, ...rest] = resolvedMe.name.trim().split(/\s+/);
            return {
                firstName,
                lastName: rest.join(" ") || undefined
            };
        }
        return userName;
    }, [resolvedMe, userName]);
    const resolvedOrganizationId = useMemo(() => {
        if (organizationId) {
            return organizationId;
        }
        if (Upper(userRole ?? "") === Upper(UserRole.ORGANIZATION_OWNER) &&
            resolvedMe?.id) {
            return resolvedMe.id;
        }
        return organizationId;
    }, [organizationId, resolvedMe, userRole]);
    useEffect(() => {
        if (isTokenExpired || !resolvedMe) {
            void auth.logout();
        }
    }, [auth, isTokenExpired, resolvedMe]);
    const contextValue = useMemo(() => ({
        isLocalhost,
        isAdminApp,
        isOrganizationApp,
        userId: resolvedUserId,
        userRole,
        userName: resolvedUserName,
        userGroups,
        organizationId: resolvedOrganizationId,
        originalOrganizationId,
        isTherapproveAdmin,
        isCaseManager,
        isMasquerading,
        isTokenExpired
    }), [
        isLocalhost,
        isAdminApp,
        isOrganizationApp,
        resolvedUserId,
        userRole,
        resolvedUserName,
        userGroups,
        resolvedOrganizationId,
        originalOrganizationId,
        isTherapproveAdmin,
        isCaseManager,
        isTokenExpired,
        isMasquerading
    ]);
    if (isTokenExpired || !resolvedMe) {
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    // Org app requires the user to belong to an organization
    if (isOrganizationApp && !resolvedOrganizationId && !isTherapproveAdmin) {
        void auth.logout();
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    // This will run on every route change for all authenticated users
    // useUpdateApp({ updateThresholdHours: 8 });
    return (_jsx(AppContext.Provider, { value: contextValue, children: _jsx(PermissionProvider, { children: _jsx(Outlet, {}) }) }));
};
